/**
 * alertHeroStyle.ts
 *
 * Shared appearance rules for the account-alert "hero" card — the promo-style
 * announcement authored in the corporate dashboard and rendered here on the
 * dashboard banner. Mirrored in corporate-dashboard/lib/alert-hero-style.ts;
 * edit both together or the builder preview stops matching what users see.
 */
import type { CSSProperties } from "react"

// ── Defaults ─────────────────────────────────────────────────────────
// An announcement with nothing configured still renders as a hero card, on
// this built-in dark gradient. An empty style column means "use the default".

export const DEFAULT_BG_COLOR = "#0f172a"
export const DEFAULT_BG_COLOR_END = "#334155"
export const DEFAULT_BUTTON_COLOR = "#ffffff"

const LIGHT_TEXT = "#ffffff"
const DARK_TEXT = "#111827"

/**
 * Gradient directions offered in the builder. The stored value is one of these
 * tokens — never raw CSS — so nothing an author types can reach a style
 * attribute. Anything unrecognised falls back to the first entry.
 */
export const ALERT_BG_DIRECTIONS = [
  { value: "diagonal", label: "Diagonal ↘", css: "135deg" },
  { value: "diagonal-up", label: "Diagonal ↗", css: "45deg" },
  { value: "horizontal", label: "Left → right", css: "to right" },
  { value: "vertical", label: "Top → bottom", css: "to bottom" },
  { value: "radial", label: "Radial glow", css: "" },
  { value: "center", label: "Center", css: "" },
] as const

/** Where the call-to-action sits in the card's right column. */
export const ALERT_BUTTON_POSITIONS = [
  { value: "auto", label: "Auto" },
  { value: "top", label: "Top" },
  { value: "middle", label: "Middle" },
  { value: "bottom", label: "Bottom" },
] as const

export type AlertButtonPosition = (typeof ALERT_BUTTON_POSITIONS)[number]["value"]

export type AlertBgDirection = (typeof ALERT_BG_DIRECTIONS)[number]["value"]

/**
 * The one font list for announcements — the title dropdown and the content
 * editor's font menu both draw from it, so authors see the same choices
 * everywhere. Titles store the token (never raw CSS); the content editor
 * writes the css stack inline, as it always has. System stacks only, so
 * nothing has to load.
 */
export const ALERT_TITLE_FONTS = [
  { value: "default", label: "Default", css: "" },
  { value: "arial", label: "Arial", css: "Arial, Helvetica, sans-serif" },
  { value: "arial-black", label: "Arial Black", css: '"Arial Black", "Avant Garde", sans-serif' },
  { value: "comic-sans", label: "Comic Sans MS", css: '"Comic Sans MS", sans-serif' },
  { value: "courier-new", label: "Courier New", css: '"Courier New", Courier, monospace' },
  { value: "georgia", label: "Georgia", css: "Georgia, Palatino, serif" },
  { value: "helvetica", label: "Helvetica", css: "Helvetica, Arial, sans-serif" },
  { value: "impact", label: "Impact", css: "Impact, Chicago, sans-serif" },
  { value: "palatino", label: "Palatino", css: '"Palatino Linotype", "Book Antiqua", Palatino, serif' },
  { value: "script", label: "Script", css: '"Segoe Script", "Brush Script MT", cursive' },
  { value: "times", label: "Times New Roman", css: '"Times New Roman", Times, serif' },
  { value: "trebuchet", label: "Trebuchet MS", css: '"Trebuchet MS", Geneva, sans-serif' },
  { value: "verdana", label: "Verdana", css: "Verdana, Geneva, sans-serif" },
] as const

/** Earlier abstract tokens, kept resolving for titles saved before the merge. */
const ALERT_FONT_ALIASES: Record<string, string> = {
  serif: "georgia",
  elegant: "palatino",
  mono: "courier-new",
}

export type AlertTitleFont = (typeof ALERT_TITLE_FONTS)[number]["value"]

// ── Colour helpers ───────────────────────────────────────────────────

const HEX_RE = /^#[0-9a-fA-F]{6}$/

/** Normalize free-typed hex: ensure a leading '#', lowercase. */
export function normalizeHexInput(raw: string): string {
  let v = (raw ?? "").trim().toLowerCase()
  if (v && !v.startsWith("#")) v = `#${v}`
  return v
}

export function isHexColor(value?: string): boolean {
  return HEX_RE.test((value ?? "").trim())
}

function hexOr(value: string | undefined, fallback: string): string {
  return isHexColor(value) ? (value as string).trim().toLowerCase() : fallback
}

/** WCAG relative luminance, used to pick readable text over a background. */
function relativeLuminance(hex: string): number {
  const n = parseInt(hex.slice(1), 16)
  const channels = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((c) => {
    const s = c / 255
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
  })
  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2]
}

/** White on dark backgrounds, near-black on light ones. */
export function readableTextColor(background: string): string {
  return relativeLuminance(hexOr(background, DEFAULT_BG_COLOR)) > 0.45 ? DARK_TEXT : LIGHT_TEXT
}

function rgba(hex: string, alpha: number): string {
  const n = parseInt(hex.slice(1), 16)
  const a = Math.round(alpha * 100) / 100
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`
}

/** Parse a stored 0-100 percentage, falling back when blank or out of range. */
function pct(value: string | undefined, fallback: number): number {
  const n = parseInt((value ?? "").trim(), 10)
  return Number.isFinite(n) && n >= 0 && n <= 100 ? n : fallback
}

// ── URL helpers ──────────────────────────────────────────────────────
// Both of these end up in markup (a CSS url() and an href), so only absolute
// http(s), site-relative and mailto values are accepted; quotes, parens and
// whitespace can never survive into the stylesheet.

const SAFE_URL_RE = /^(https?:\/\/|\/)[^\s"'()\\<>]*$/i
const BARE_DOMAIN_RE = /^[a-z0-9-]+(\.[a-z0-9-]+)+(\/[^\s"'()\\<>]*)?$/i

export function safeImageUrl(raw?: string): string {
  const v = (raw ?? "").trim()
  return SAFE_URL_RE.test(v) ? v : ""
}

/** Same rules as the image, plus mailto: and an https:// prefix for bare domains. */
export function safeLinkUrl(raw?: string): string {
  const v = (raw ?? "").trim()
  if (SAFE_URL_RE.test(v)) return v
  if (/^mailto:[^\s"'()\\<>]+$/i.test(v)) return v
  if (BARE_DOMAIN_RE.test(v)) return `https://${v}`
  return ""
}

// ── Rich text helpers ────────────────────────────────────────────────

/** Whitespace-only entities the editors leave behind in otherwise empty HTML. */
const BLANK_ENTITY_RE = /&nbsp;|&#160;|&#xa0;/gi

/** Tags that are worth showing even with no text alongside them. */
const MEDIA_TAG_RE = /<(img|iframe|video|embed|table|hr)\b/i

/** The visible text of rich-text HTML, collapsed. '' when there is nothing to show. */
export function richTextToPlain(html?: string): string {
  return (html ?? "")
    .replace(BLANK_ENTITY_RE, " ")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim()
}

/**
 * Whether rich-text HTML actually has something to show. A field an author
 * cleared comes back from the editor as "<p></p>" or "<p><br></p>", which is a
 * truthy string — so a plain truthiness check lights up a "More Details" link
 * over an empty modal.
 */
export function hasRichTextContent(html?: string): boolean {
  const value = (html ?? "").trim()
  if (!value) return false
  if (MEDIA_TAG_RE.test(value)) return true
  return richTextToPlain(value).length > 0
}

/**
 * Whether "More Details" holds anything the card is not already showing. False
 * when it is empty, and false when it only repeats the announcement copy — in
 * both cases the link opens a modal that tells the reader nothing new.
 */
export function hasExtraDetails(alertContent?: string, moreDetails?: string): boolean {
  if (!hasRichTextContent(moreDetails)) return false
  const details = richTextToPlain(moreDetails).toLowerCase()
  if (!details) return true // media-only details still add something
  return details !== richTextToPlain(alertContent).toLowerCase()
}


// ── Title markup ─────────────────────────────────────────────────────
// The title is authored in a mini WYSIWYG: bold / italic / underline and
// colour apply to the selection, so alertTitle stores inline HTML. Only the
// tags below survive sanitizing — everything else is unwrapped to its text.

function escapeHtml(text: string): string {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
}

/** '#rrggbb' from a hex or rgb()/rgba() colour; '' for anything else. */
function cssColorToHex(value: string): string {
  const v = (value ?? "").trim().toLowerCase()
  if (HEX_RE.test(v)) return v
  const m = v.match(/^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/)
  if (!m) return ""
  return (
    "#" +
    m
      .slice(1, 4)
      .map((n) => Math.min(255, parseInt(n, 10)).toString(16).padStart(2, "0"))
      .join("")
  )
}

const TITLE_TAGS: Record<string, string> = {
  B: "b", STRONG: "b", I: "i", EM: "i", U: "u", SPAN: "span", FONT: "span",
}

/**
 * Reduce title HTML to the inline whitelist: <b>, <i>, <u> and
 * <span style="color:#rrggbb">. Unknown tags are unwrapped to their text,
 * text nodes are re-escaped, and editor colour output (rgb() spans, legacy
 * <font color>) is normalised to hex spans — so the stored markup is the
 * same whichever browser authored it, and nothing else can reach the
 * dangerouslySetInnerHTML that renders it.
 */
export function sanitizeTitleHtml(html?: string): string {
  const value = (html ?? "").trim()
  if (!value) return ""
  if (typeof document === "undefined" || typeof DOMParser === "undefined") {
    return escapeHtml(richTextToPlain(value))
  }
  const walk = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) return escapeHtml(node.textContent ?? "")
    if (node.nodeType !== Node.ELEMENT_NODE) return ""
    const el = node as HTMLElement
    const inner = Array.from(el.childNodes).map(walk).join("")
    const tag = TITLE_TAGS[el.tagName]
    if (!tag) return inner
    if (tag === "span") {
      if (!inner) return ""
      // Browsers with styleWithCSS write formatting as span styles instead of
      // <b>/<i>/<u> tags - normalise those back to tags so they survive.
      let out = inner
      const fw = el.style?.fontWeight ?? ""
      if (fw === "bold" || fw === "bolder" || parseInt(fw, 10) >= 600) out = `<b>${out}</b>`
      if ((el.style?.fontStyle ?? "").includes("italic")) out = `<i>${out}</i>`
      const deco = `${el.style?.textDecoration ?? ""} ${el.style?.textDecorationLine ?? ""}`
      if (deco.includes("underline")) out = `<u>${out}</u>`
      const hex = cssColorToHex(el.style?.color || el.getAttribute("color") || "")
      return hex ? `<span style="color:${hex}">${out}</span>` : out
    }
    return inner ? `<${tag}>${inner}</${tag}>` : ""
  }
  const doc = new DOMParser().parseFromString(value, "text/html")
  return Array.from(doc.body.childNodes).map(walk).join("").trim()
}

// ── Resolver ─────────────────────────────────────────────────────────

/** The account_alerts columns that drive the hero card appearance. */
export interface AlertHeroStyleInput {
  bgColor?: string
  bgColorEnd?: string
  bgDirection?: string
  bgImage?: string
  /** Scrim strength over a background photo, 0-100. 100 hides the photo. '' = 90. */
  bgOverlayOpacity?: string
  /** Share of the card held at full strength before fading out, 0-100. '' = 25. */
  bgOverlayCoverage?: string
  textColor?: string
  /** Token from ALERT_TITLE_FONTS, applied to the whole title. Bold/italic/
   *  underline and colour are inline markup in alertTitle itself. */
  titleFont?: string
  buttonText?: string
  buttonURL?: string
  buttonColor?: string
  buttonTextColor?: string
  /** Token from ALERT_BUTTON_POSITIONS. '' / unknown = "auto". */
  buttonPosition?: string
}

export interface AlertHeroStyle {
  /** Inline background for the card: gradient, photo + scrim, or solid. */
  container: CSSProperties
  /** Title colour, readable against whatever the background resolved to. */
  textColor: string
  /** Base style for the title: its default colour plus the authored font. */
  title: CSSProperties
  /** The same colour softened, for body copy and meta lines. */
  mutedTextColor: string
  /** Hairline/divider colour derived from the text colour. */
  borderColor: string
  /** True when the resolved text colour is the light one, i.e. the card reads dark. */
  isLightText: boolean
  /** True when nothing is configured and the built-in look is in use. */
  isDefault: boolean
  /** Present only once the author has given the button a label. */
  button: { text: string; url: string; style: CSSProperties; position: AlertButtonPosition } | null
}

/**
 * Turn the stored style columns into inline styles. The corporate-dashboard
 * builder preview and this dashboard banner both call it, so the card an author
 * previews is the card a user sees.
 */
export function resolveAlertHeroStyle(input: AlertHeroStyleInput): AlertHeroStyle {
  const hasStart = isHexColor(input.bgColor)
  const start = hexOr(input.bgColor, DEFAULT_BG_COLOR)
  // A configured start colour with no end colour means a flat fill; only the
  // untouched default gets the built-in two-stop gradient.
  const end = isHexColor(input.bgColorEnd)
    ? (input.bgColorEnd as string).trim().toLowerCase()
    : hasStart
      ? ""
      : DEFAULT_BG_COLOR_END
  const image = safeImageUrl(input.bgImage)

  const requested = (input.bgDirection ?? "").trim()
  const direction = ALERT_BG_DIRECTIONS.find((d) => d.value === requested) ?? ALERT_BG_DIRECTIONS[0]

  let container: CSSProperties
  if (image) {
    // Photo backgrounds sit under a scrim tinted with the background colour so
    // the copy stays legible whatever the photo is. Two authored numbers drive
    // it, and both read literally:
    //   strength - how opaque the shade is where it is at full force. At 100
    //              it is the background colour, with no photo showing through.
    //   coverage - how much of the card is held at that full force before the
    //              shade fades away to nothing. At 100 it is the whole card.
    // So 100/100 is a solid block of the background colour, and lowering either
    // number lets more of the photo through. Coverage is measured along the
    // authored gradient direction: from the leading edge for the linear ones,
    // outward from the middle for "center", inward from the rim for "radial".
    const peak = pct(input.bgOverlayOpacity, 90) / 100
    const coverage = pct(input.bgOverlayCoverage, 25)
    const solid = rgba(start, peak)
    const clear = rgba(start, 0)
    let scrim: string
    if (direction.value === "radial") {
      // Vignette: clear core, shade closing in from the edges.
      scrim = `radial-gradient(circle, ${clear} 0%, ${solid} ${100 - coverage}%, ${solid} 100%)`
    } else if (direction.value === "center") {
      // A band across the middle fading toward both edges; coverage is how
      // wide the full-strength band is.
      const half = coverage / 2
      scrim = `linear-gradient(to right, ${clear} 0%, ${solid} ${50 - half}%, ${solid} ${50 + half}%, ${clear} 100%)`
    } else {
      scrim = `linear-gradient(${direction.css}, ${solid} 0%, ${solid} ${coverage}%, ${clear} 100%)`
    }
    container = {
      backgroundImage: `${scrim}, url("${image}")`,
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
    }
  } else if (end) {
    container = {
      backgroundImage:
        direction.value === "radial"
          ? `radial-gradient(circle at 30% 20%, ${end}, ${start})`
          : direction.value === "center"
            ? `linear-gradient(to right, ${end} 0%, ${start} 50%, ${end} 100%)`
            : `linear-gradient(${direction.css}, ${start}, ${end})`,
    }
  } else {
    container = { backgroundColor: start }
  }

  const textColor = isHexColor(input.textColor)
    ? (input.textColor as string).trim().toLowerCase()
    : readableTextColor(start)
  const isLightText = relativeLuminance(textColor) > 0.45

  const requestedFont = (input.titleFont ?? "").trim()
  const titleFont =
    ALERT_TITLE_FONTS.find((f) => f.value === (ALERT_FONT_ALIASES[requestedFont] ?? requestedFont)) ??
    ALERT_TITLE_FONTS[0]
  const title: CSSProperties = { color: textColor }
  if (titleFont.css) title.fontFamily = titleFont.css

  const buttonText = (input.buttonText ?? "").trim()
  const buttonBg = hexOr(input.buttonColor, DEFAULT_BUTTON_COLOR)
  const buttonFg = isHexColor(input.buttonTextColor)
    ? (input.buttonTextColor as string).trim().toLowerCase()
    : readableTextColor(buttonBg)

  return {
    container,
    textColor,
    title,
    mutedTextColor: isLightText ? "rgba(255, 255, 255, 0.82)" : "rgba(17, 24, 39, 0.75)",
    borderColor: isLightText ? "rgba(255, 255, 255, 0.22)" : "rgba(17, 24, 39, 0.15)",
    isLightText,
    isDefault: !hasStart && !isHexColor(input.bgColorEnd) && !image,
    button: buttonText
      ? {
          text: buttonText,
          url: safeLinkUrl(input.buttonURL),
          style: { backgroundColor: buttonBg, color: buttonFg },
          position:
            ALERT_BUTTON_POSITIONS.find((bp) => bp.value === (input.buttonPosition ?? "").trim())?.value ?? "auto",
        }
      : null,
  }
}
